import api from '../../lib/api';

/**
 * El POST de alta devuelve el registro en la raíz (Prisma); el GET envuelve en `{ data }`.
 * Unifica a `AssessmentRecipientRow` para el store.
 */
function normalizeRecipientFromApi(raw: unknown): AssessmentRecipientRow {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Respuesta inválida al vincular destinatario');
  }
  const o = raw as Record<string, unknown>;
  const ext =
    typeof o.externalRecipientId === 'string'
      ? o.externalRecipientId
      : o.externalRecipient &&
          typeof o.externalRecipient === 'object' &&
          o.externalRecipient !== null &&
          'id' in o.externalRecipient
        ? String((o.externalRecipient as { id: string }).id)
        : '';
  if (!ext) {
    throw new Error('Respuesta inválida al vincular destinatario');
  }
  const createdRaw = o.createdAt;
  const createdAt =
    typeof createdRaw === 'string'
      ? createdRaw
      : createdRaw instanceof Date
        ? createdRaw.toISOString()
        : new Date().toISOString();
  if (!o.externalRecipient || typeof o.externalRecipient !== 'object') {
    throw new Error('Respuesta inválida al vincular destinatario');
  }
  return {
    id: typeof o.id === 'string' ? o.id : ext,
    externalRecipientId: ext,
    assessmentId: typeof o.assessmentId === 'string' ? o.assessmentId : '',
    createdAt,
    externalRecipient: o.externalRecipient as AssessmentRecipientRow['externalRecipient'],
    tokenGeneratedAt: typeof o.tokenGeneratedAt === 'string' ? o.tokenGeneratedAt : undefined,
    tokenSentAt: typeof o.tokenSentAt === 'string' ? o.tokenSentAt : undefined,
  };
}

export interface AssessmentRecipientRow {
  id: string;
  externalRecipientId: string;
  assessmentId: string;
  createdAt: string;
  externalRecipient: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    role: string;
    active: boolean;
    createdAt: string;
    updatedAt: string;
  };
  tokenGeneratedAt?: string;
  tokenSentAt?: string;
}

export interface AssessmentRecipientsResponse {
  data: AssessmentRecipientRow[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
}

export interface AddRecipientPayload {
  externalRecipientId: string;
}

export interface GenerateMassResult {
  generated: number;
  skipped: number;
}

export interface SendMassResult {
  success: boolean;
  sent: number;
  failed: number;
}

export const fetchAssessmentRecipients = async (
  assessmentId: string
): Promise<AssessmentRecipientsResponse> => {
  const { data } = await api.get<{ success: boolean; data: AssessmentRecipientRow[] }>(
    `assessments/${assessmentId}/recipients`
  );
  return {
    data: data.data,
    pagination: { page: 1, pageSize: 100, total: data.data.length, pages: 1 },
  };
};

export const addAssessmentRecipient = async (
  assessmentId: string,
  payload: AddRecipientPayload
): Promise<AssessmentRecipientRow> => {
  try {
    const { data } = await api.post<unknown>(`assessments/${assessmentId}/recipients`, payload);
    const raw =
      data &&
      typeof data === 'object' &&
      'data' in (data as object) &&
      (data as { data?: unknown }).data !== undefined
        ? (data as { data: unknown }).data
        : data;
    return normalizeRecipientFromApi(raw);
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const removeAssessmentRecipient = async (
  assessmentId: string,
  recipientId: string
): Promise<void> => {
  try {
    await api.delete(`assessments/${assessmentId}/recipients/${recipientId}`);
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const clearAssessmentRecipients = async (assessmentId: string): Promise<void> => {
  try {
    await api.delete(`assessments/${assessmentId}/recipients`);
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const generateMassTokens = async (
  assessmentId: string
): Promise<GenerateMassResult> => {
  try {
    const { data } = await api.post<{ success: boolean; data: GenerateMassResult }>(
      `assessments/${assessmentId}/tokens/generate-mass`
    );
    return data.data;
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const sendMassTokens = async (
  assessmentId: string
): Promise<SendMassResult> => {
  try {
    const { data } = await api.post<{ success: boolean; data: SendMassResult }>(
      `assessments/${assessmentId}/tokens/send-mass`
    );
    return data.data;
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export interface SendSingleResult {
  success: boolean;
  sent?: boolean;
  alreadySent?: boolean;
  message?: string;
  error?: string;
}

/** POST send devuelve `{ success, sent, message }` en la raíz; acepta también `{ data: ... }`. */
function unwrapSendSinglePayload(data: unknown): SendSingleResult {
  if (!data || typeof data !== 'object') {
    return { success: false, error: 'Respuesta inválida' };
  }
  const d = data as Record<string, unknown>;
  const inner = d.data !== undefined && typeof d.data === 'object' ? (d.data as Record<string, unknown>) : d;
  return {
    success: Boolean(inner.success),
    sent: inner.sent === true,
    alreadySent: inner.alreadySent === true,
    message: typeof inner.message === 'string' ? inner.message : undefined,
    error: typeof inner.error === 'string' ? inner.error : undefined,
  };
}

export const sendSingleToken = async (
  assessmentId: string,
  recipientId: string
): Promise<SendSingleResult> => {
  try {
    const { data } = await api.post<unknown>(
      `assessments/${assessmentId}/recipients/${recipientId}/send`
    );
    return unwrapSendSinglePayload(data);
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};